import { vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { Series } from '~/domain/dicom/series';
import {
    sliceGeometryMismatch,
    sliceStackFrame,
    stackOrientation,
} from '~/domain/services/sliceStackFrame';

export interface AssembledVolume {
    pixels: Int16Array;
    grid: Grid;
    /**
     * Extent along the stack that holds a duplicated neighbour rather than
     * an acquired slice, mm.
     *
     * In MILLIMETRES, not in slices. This volume is assembled at the
     * SOURCE's spacing and then resampled to the thickness the user chose,
     * so a count of slices here is a count in a grid neither the user nor
     * the output uses — 4 slices of a 1 mm source is 4 mm, which is not 4
     * slices of a 3 mm output. Measured here because this is the only
     * place that knows.
     */
    gapFilledMm: number;
}

/**
 * Assemble a regular 3D volume from raw DICOM slices that may have
 * non-uniform spacing. Sorts slices by Z projection, detects the
 * base spacing, and fills gaps using nearest-neighbor duplication.
 *
 * This handles the case where `Image3D.fromSeries()` fails due to
 * ResamplingNeeded or excessive MissingSlices.
 *
 * The whole series is assembled. Selecting a sub-range is the caller's
 * business and belongs to the geometry it resamples into: dropping slices
 * here would have to decide what "inside the range" means for a slice
 * whose plane is not perpendicular to the range's axis, which has no
 * answer for a tilted or non-transversal acquisition.
 */
export function assembleVolumeFromSlices(series: Series): AssembledVolume {
    const slices = series.dicomImageSlices;

    if (slices.length === 0) {
        throw new Error('Cannot assemble volume: no slices in series');
    }

    const frame = sliceStackFrame(slices);
    const { ordered, along, dx, dy, spacing } = frame;
    const nz = frame.sliceCount;
    const nx = ordered[0].cols;
    const ny = ordered[0].rows;

    // The same test `reformatRegionRefusal` shows the user, so a series
    // this cannot assemble is refused before the reformat starts rather
    // than failing halfway through it.
    const mismatch = sliceGeometryMismatch(ordered);
    if (mismatch !== null) {
        throw new Error(`Cannot assemble volume: slice ${mismatch}`);
    }

    // Voxel index k lands where slice k actually is, because the grid's
    // third axis is the direction the stack steps in. Where that is not the
    // slice normal the basis is not orthonormal — deliberately, because the
    // stack is not. `Grid.fromDicom` copies the basis verbatim and shifts
    // the given voxel CENTRE to the corner through it, which is correct for
    // a non-orthonormal basis too.
    const grid = Grid.fromDicom(
        vec3.fromValues(nx, ny, nz),
        vec3.fromValues(dx, dy, spacing),
        frame.firstPosition,
        stackOrientation(frame)
    );

    const sliceSize = nx * ny;
    const pixels = new Int16Array(nx * ny * nz);
    // Half a spacing: beyond that the nearest acquired slice is not the one
    // that belongs at this position, so the voxels are a duplicate rather
    // than the measurement.
    const nearEnough = spacing / 2;
    let gapFilled = 0;

    // Each output index takes the nearest acquired slice along the step
    // direction, which duplicates a neighbour across a gap.
    for (let zi = 0; zi < nz; zi++) {
        const target = zi * spacing;
        let bestIdx = 0;
        let bestDist = Math.abs(along[0] - target);
        for (let i = 1; i < along.length; i++) {
            const dist = Math.abs(along[i] - target);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }
        if (bestDist > nearEnough) {
            gapFilled++;
        }
        pixels.set(ordered[bestIdx].pixelData, zi * sliceSize);
    }

    return { pixels, grid, gapFilledMm: gapFilled * spacing };
}

/**
 * Compute the world-space axis-aligned bounding box of a set of
 * DICOM image slices.
 *
 * Edge to edge IN PLANE only. `ImagePositionPatient` is the centre of the
 * first voxel, so each slice's rectangle is extended by half a pixel along
 * its ROW and COLUMN directions — and along neither of those does a slice
 * have thickness. ACROSS the stack the box therefore spans the outermost
 * slice CENTRES, half a slice short of the voxel extent at each end.
 *
 * That is a property callers depend on in both directions, so it is not a
 * gap to close here:
 *
 * - {@link defaultRegionForSlices} wants the centre range, because
 *   `ReformatRegion.zRangeMm` is a pair of slice centres. On a transversal
 *   acquisition the in-plane directions carry no Z, so this returns
 *   exactly that.
 * - {@link reformatSourceBoundsMm} wants the voxel extent, and adds the
 *   missing half slice along the direction the stack steps in — which is
 *   patient Z only for a transversal acquisition.
 */
export function computeSliceAABB(slices: DicomImageSlice[]): {
    worldMin: vec3;
    worldMax: vec3;
} {
    if (slices.length === 0) {
        return {
            worldMin: vec3.create(),
            worldMax: vec3.create(),
        };
    }

    const worldMin = vec3.fromValues(+Infinity, +Infinity, +Infinity);
    const worldMax = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    for (const slice of slices) {
        const pos = slice.imagePosition;
        const orientation = slice.imageOrientation;
        const dx = slice.pixelSpacing[0];
        const dy = slice.pixelSpacing[1];
        const nx = slice.cols;
        const ny = slice.rows;

        // Row and column directions
        const rowDir = vec3.fromValues(
            orientation[0],
            orientation[1],
            orientation[2]
        );
        const colDir = vec3.fromValues(
            orientation[3],
            orientation[4],
            orientation[5]
        );

        // Voxel-center origin shifted to the corner of the first voxel
        const corner = vec3.scaleAndAdd(
            vec3.create(),
            pos as vec3,
            rowDir,
            -0.5 * dx
        );
        vec3.scaleAndAdd(corner, corner, colDir, -0.5 * dy);

        // Compute 4 edge-to-edge corners of this slice
        const corners = [
            vec3.clone(corner),
            vec3.scaleAndAdd(vec3.create(), corner, rowDir, nx * dx),
            vec3.scaleAndAdd(vec3.create(), corner, colDir, ny * dy),
            vec3.create(),
        ];
        vec3.scaleAndAdd(corners[3], corner, rowDir, nx * dx);
        vec3.scaleAndAdd(corners[3], corners[3], colDir, ny * dy);

        for (const c of corners) {
            vec3.min(worldMin, worldMin, c);
            vec3.max(worldMax, worldMax, c);
        }
    }

    return { worldMin, worldMax };
}

/**
 * Spacing between slices along the direction the stack steps in, mm.
 *
 * Delegates to {@link sliceStackFrame} so this and the assembled volume
 * cannot describe different spacings, which they otherwise do on any stack
 * stepping off its normal.
 */
export function computeMedianSpacing(slices: DicomImageSlice[]): number {
    if (slices.length < 2) {
        return slices[0]?.sliceThickness || 1.0;
    }
    return sliceStackFrame(slices).spacing;
}
